import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Users, ChevronRight, Clock, Image as ImageIcon, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function useTimeRemaining(t) {
  return (expIso) => {
    const ms = new Date(expIso) - new Date();
    if (ms <= 0) return t("detail.expired");
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m`;
  };
}

export default function SalesAllSimulationsPage() {
  const { t } = useTranslation();
  const timeRemaining = useTimeRemaining(t);
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ type: "ALL", status: "ALL", q: "" });

  useEffect(() => {
    api
      .get("/simulations")
      .then((r) => setSims(r.data))
      .catch((e) =>
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("sales_all.load_error")),
      )
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = sims.filter((s) => {
    if (filter.type !== "ALL" && s.sim_type !== filter.type) return false;
    if (filter.status !== "ALL" && s.status !== filter.status) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      const blob = `${s.user_email} ${s.client_name || ""} ${s.client_email || ""} ${s.style}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="text-orange-500 animate-spin" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sales-all-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20">
        <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-3 flex items-center gap-2">
              <Users size={12} strokeWidth={1.5} /> {t("sales_all.eyebrow")}
            </div>
            <h1 className="font-outfit font-black text-white text-5xl md:text-6xl tracking-tighter leading-none">
              {t("sales_all.title")}
            </h1>
            <p className="text-zinc-400 mt-3">
              {t("sales_all.subtitle")}
            </p>
          </div>
          <div className="text-right">
            <div className="font-outfit text-3xl font-black text-orange-500">
              {filtered.length}
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
              {t("sales_all.results_label")}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="grid md:grid-cols-3 gap-3 mb-8" data-testid="filters">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-zinc-500" />
            <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
              <SelectTrigger className="bg-zinc-900/50 border-white/10" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-white">
                <SelectItem value="ALL">{t("sales_all.filter_type_all")}</SelectItem>
                <SelectItem value="INTERIOR">{t("sales_all.filter_type_interior")}</SelectItem>
                <SelectItem value="EXTERIOR">{t("sales_all.filter_type_exterior")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="bg-zinc-900/50 border-white/10" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-white/10 text-white">
              <SelectItem value="ALL">{t("sales_all.filter_status_all")}</SelectItem>
              <SelectItem value="completed">{t("sales_all.filter_status_completed")}</SelectItem>
              <SelectItem value="generating">{t("sales_all.filter_status_generating")}</SelectItem>
              <SelectItem value="pending">{t("sales_all.filter_status_pending")}</SelectItem>
              <SelectItem value="failed">{t("sales_all.filter_status_failed")}</SelectItem>
            </SelectContent>
          </Select>
          <Input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            placeholder={t("sales_all.search_placeholder")}
            className="bg-zinc-900/50 border-white/10"
            data-testid="filter-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="border border-white/10 rounded-lg bg-zinc-900/20 p-16 text-center">
            <ImageIcon size={32} className="text-zinc-600 mx-auto mb-3" />
            <div className="text-zinc-400">{t("sales_all.no_results")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sims-grid">
            {filtered.map((s) => {
              const cover = s.generated_images?.[0]?.url || s.source_image_url;
              return (
                <Link
                  key={s.id}
                  to={`/simulation/${s.id}`}
                  className="group relative bg-zinc-900/30 border border-white/10 rounded-lg overflow-hidden hover:border-orange-500/40 transition-colors"
                  data-testid={`sim-card-${s.id}`}
                >
                  <div className="aspect-[4/3] bg-zinc-950 relative overflow-hidden">
                    {cover && (
                      <img
                        src={fileUrl(cover)}
                        alt={s.id}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                    <div className="absolute top-3 left-3 flex gap-2">
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur text-orange-400 rounded">
                        {s.sim_type}
                      </span>
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur text-zinc-300 rounded">
                        {s.style.replace("_", " ")}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span
                        className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] rounded ${
                          s.status === "completed"
                            ? "bg-green-500/20 text-green-400"
                            : s.status === "failed"
                            ? "bg-red-500/20 text-red-400"
                            : "bg-orange-500/20 text-orange-400"
                        }`}
                      >
                        {s.status}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-sm font-medium text-white truncate">
                      {s.user_email}
                    </div>
                    {s.client_name && (
                      <div className="text-xs text-zinc-400 mt-1 truncate">
                        {t("sales_all.client_label")} {s.client_name}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {timeRemaining(s.expires_at)}
                      </span>
                      <span className="flex items-center gap-1 text-orange-400 group-hover:translate-x-1 transition-transform">
                        {t("sales_all.details_label")} <ChevronRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
